/**
 * AboutUs UI Logic
 * All UI-related logic: canvas animation, state management
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for managing AboutUs canvas animation
 * @param {boolean} isLoading - Whether the page is still loading
 * @returns {{initWavyCanvas: function, cleanup: function}}
 */
export function useAboutUsCanvas(isLoading = false) {
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const animationFrameRef = useRef(null);
  const timeRef = useRef(0);

  const initWavyCanvas = useCallback(() => {
    const canvas = document.getElementById('aboutUsWavyCanvas');
    if (!canvas) {
      console.log('About Us wavy canvas not found!');
      return null;
    }

    const ctx = canvas.getContext('2d');
    const curvature = -0.12;
    let lastCssWidth = 0;
    let lastCssHeight = 0;
    let lastDpr = 0;

    const getContainerSize = () => {
      const container = canvas.parentElement;
      if (!container) return null;

      const rect = container.getBoundingClientRect();
      const cssWidth = Math.max(1, Math.round(rect.width || container.offsetWidth || container.clientWidth || window.innerWidth));
      const cssHeight = Math.max(1, Math.round(rect.height || container.offsetHeight || container.clientHeight || window.innerHeight));
      return { cssWidth, cssHeight };
    };

    function resizeCanvas(force = false) {
      const dpr = window.devicePixelRatio || 1;
      const size = getContainerSize();
      if (!size) return false;

      const { cssWidth, cssHeight } = size;
      const nextWidth = Math.max(1, Math.round(cssWidth * dpr));
      const nextHeight = Math.max(1, Math.round(cssHeight * dpr));

      const hasChanged =
        force ||
        cssWidth !== lastCssWidth ||
        cssHeight !== lastCssHeight ||
        Math.abs(dpr - lastDpr) > 0.001 ||
        canvas.width !== nextWidth ||
        canvas.height !== nextHeight;

      if (!hasChanged) {
        return false;
      }

      canvas.width = nextWidth;
      canvas.height = nextHeight;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;

      // Set transform in one step to avoid compounding scales.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      lastCssWidth = cssWidth;
      lastCssHeight = cssHeight;
      lastDpr = dpr;
      return true;
    }

    function drawCurvePattern() {
      resizeCanvas();

      if (lastCssWidth === 0 || lastCssHeight === 0) {
        return;
      }

      ctx.clearRect(0, 0, lastCssWidth, lastCssHeight);

      const containerHeight = lastCssHeight;
      const containerWidth = lastCssWidth;
      
      // Update responsive settings on each draw (in case of resize)
      const currentIsMobile = window.innerWidth <= 768;
      
      // Desktop settings - original pattern
      const currentDensity = 0.85;
      const currentNumLines = 20;
      const currentLineWidth = 1.5;
      const currentGradientLineWidth = 1;
      
      // On mobile, don't draw anything - it looks cluttered
      if (currentIsMobile) {
        return;
      }
      
      // Draw wavy lines from bottom to top with proper mirroring
      for (let i = 0; i <= currentNumLines; i++) {
        const t = i / currentNumLines;
        const startY = containerHeight;
        const waveOffset = Math.sin(timeRef.current * 0.001 + t * Math.PI * 2) * 30;
        const curveY = containerHeight * (1 - Math.pow(t, 2)) * curvature;
        const endY = startY - (1 - t) * startY + curveY;

        if (i % Math.round(1 / currentDensity) === 0) {
          // Left side lines
          const leftStartX = t * containerWidth;
          const leftCurveX = containerWidth * Math.pow(1 - t, 2) * curvature + waveOffset;
          const leftEndX = leftCurveX + Math.sin(timeRef.current * 0.001 + t * Math.PI * 3) * 15;

          // Right side lines
          const rightStartX = containerWidth - (t * containerWidth);
          const rightCurveX = containerWidth - (containerWidth * Math.pow(1 - t, 2) * curvature) - waveOffset;
          const rightEndX = rightCurveX - Math.sin(timeRef.current * 0.001 + t * Math.PI * 3) * 15;

          // Check if dark mode is active
          const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
          
          // Left side colors
          let leftLineColor, leftGradientColor1, leftGradientColor2;
          if (isDarkMode) {
            leftLineColor = 'rgba(88, 151, 222, 0.6)';
            leftGradientColor1 = 'rgba(88, 151, 222, 0.5)';
            leftGradientColor2 = 'rgba(88, 151, 222, 0.1)';
          } else {
            // Light blue for light mode
            leftLineColor = 'rgba(96, 165, 250, 0.6)'; // light blue-400
            leftGradientColor1 = 'rgba(96, 165, 250, 0.5)';
            leftGradientColor2 = 'rgba(96, 165, 250, 0.15)';
          }

          // Right side colors
          let rightLineColor, rightGradientColor1, rightGradientColor2;
          if (isDarkMode) {
            rightLineColor = 'rgba(88, 151, 222, 0.6)';
            rightGradientColor1 = 'rgba(88, 151, 222, 0.5)';
            rightGradientColor2 = 'rgba(88, 151, 222, 0.1)';
          } else {
            // Light blue for light mode
            rightLineColor = 'rgba(96, 165, 250, 0.6)'; // light blue-400
            rightGradientColor1 = 'rgba(96, 165, 250, 0.5)';
            rightGradientColor2 = 'rgba(96, 165, 250, 0.15)';
          }

          // Draw left side curved line
          ctx.strokeStyle = leftLineColor;
          ctx.lineWidth = currentLineWidth;
          ctx.beginPath();
          ctx.moveTo(leftStartX, containerHeight);
          ctx.lineTo(leftEndX, endY);
          ctx.stroke();

          // Draw left side vertical gradient line
          const leftGradient = ctx.createLinearGradient(leftEndX, endY, leftEndX, containerHeight);
          leftGradient.addColorStop(0, leftGradientColor1);
          leftGradient.addColorStop(1, leftGradientColor2);
          ctx.strokeStyle = leftGradient;
          ctx.lineWidth = currentGradientLineWidth;
          ctx.beginPath();
          ctx.moveTo(leftEndX, endY);
          ctx.lineTo(leftEndX, containerHeight);
          ctx.stroke();

          // Draw right side curved line (mirrored)
          ctx.strokeStyle = rightLineColor;
          ctx.lineWidth = currentLineWidth;
          ctx.beginPath();
          ctx.moveTo(rightStartX, containerHeight);
          ctx.lineTo(rightEndX, endY);
          ctx.stroke();

          // Draw right side vertical gradient line (mirrored)
          const rightGradient = ctx.createLinearGradient(rightEndX, endY, rightEndX, containerHeight);
          rightGradient.addColorStop(0, rightGradientColor1);
          rightGradient.addColorStop(1, rightGradientColor2);
          ctx.strokeStyle = rightGradient;
          ctx.lineWidth = currentGradientLineWidth;
          ctx.beginPath();
          ctx.moveTo(rightEndX, endY);
          ctx.lineTo(rightEndX, containerHeight);
          ctx.stroke();
        }
      }
    }

    function animate() {
      timeRef.current += 2;
      drawCurvePattern();

      animationFrameRef.current = requestAnimationFrame(animate);
    }

    function initPage() {
      resizeCanvas(true);
      
      if (canvas.width === 0 || canvas.height === 0) {
        return;
      }

      animate();
    }

    const handleResize = () => {
      resizeCanvas(true);
    };

    // Handle zoom changes - visual viewport API for zoom detection
    const handleZoom = () => {
      resizeCanvas(true);
    };

    // Use ResizeObserver to detect container size changes (catches zoom)
    let resizeObserver = null;
    const container = canvas.parentElement;
    if (container && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        resizeCanvas(true);
      });
      resizeObserver.observe(container);
    }

    // Listen to window resize events
    window.addEventListener('resize', handleResize);
    
    // Listen to visual viewport changes (zoom, mobile keyboard, etc.)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleZoom);
      window.visualViewport.addEventListener('scroll', handleZoom);
    }
    
    initPage();

    // Return cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleZoom);
        window.visualViewport.removeEventListener('scroll', handleZoom);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Don't initialize if still loading (canvas element doesn't exist yet)
    if (isLoading) {
      return;
    }
    
    let cleanupFn = null;
    let retryTimer1 = null;
    let retryTimer2 = null;
    
    // Function to initialize canvas
    const initializeCanvas = () => {
      const canvas = document.getElementById('aboutUsWavyCanvas');
      if (!canvas) {
        console.warn('About Us canvas element not found');
        return null;
      }
      
      // Check if container has dimensions
      const container = canvas.parentElement;
      if (container && (container.offsetHeight === 0 || container.offsetWidth === 0)) {
        return null;
      }
      
      cleanupFn = initWavyCanvas();
      
      // Ensure text is visible immediately
      if (titleRef.current) {
        titleRef.current.style.opacity = '1';
        titleRef.current.style.transform = 'translateY(0)';
      }
      if (subtitleRef.current) {
        subtitleRef.current.style.opacity = '1';
        subtitleRef.current.style.transform = 'translateY(0)';
      }
      
      return cleanupFn;
    };
    
    // Try to initialize with retries
    cleanupFn = initializeCanvas();
    
    if (!cleanupFn) {
      retryTimer1 = setTimeout(() => {
        cleanupFn = initializeCanvas();
        if (!cleanupFn) {
          retryTimer2 = setTimeout(() => {
            cleanupFn = initializeCanvas();
          }, 300);
        }
      }, 200);
    }

    return () => {
      if (retryTimer1) clearTimeout(retryTimer1);
      if (retryTimer2) clearTimeout(retryTimer2);
      if (cleanupFn) {
        cleanupFn();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [initWavyCanvas, isLoading]);

  return {
    titleRef,
    subtitleRef
  };
}
